import type { SceneObject } from "../../SceneHost";
import { isPdfDocumentObject } from "../../document/documentContext";
import { createLazyThumbStrip, type LazyThumbStrip } from "../../../lib/board/lazyThumbStrip";


/**
 * Презентация рисуется обычным HTML в слое доски, а не внутри SVG.
 *
 * Safari (а на iPhone и iPad движок один у всех браузеров) не применяет к HTML
 * внутри SVG преобразования сцены: объект оказывается в левом верхнем углу
 * видимой части доски, а не на своём месте, и две презентации наезжают одна
 * на другую. Проверено на движке WebKit: тот же HTML, положенный в слой
 * камеры напрямую, рисуется там же, где и в Chrome.
 */

/** Ширина полоски слайдов справа от PDF (в мировых единицах). */
export const PDF_THUMBS_WIDTH = 176;

/** Зазор между правым краем слайда и полоской. */
export const PDF_THUMBS_GAP = 10;

/**
 * Докуда вправо презентация достаёт вместе с полоской слайдов.
 * Полоска висит за краем объекта и в его ширину не входит, а поверхность
 * слоя сцены обязана её вместить: иначе SVG обрезает ленту по своему краю —
 * миниатюры видно наполовину.
 */
export function pdfSurfaceRight(obj: SceneObject): number {
  const x = Number(obj.x) || 0;
  const w = typeof obj.w === "number" ? obj.w : 640;
  return x + w + PDF_THUMBS_GAP + PDF_THUMBS_WIDTH;
}

/** Ленивые картинки полоски слайдов — по одной ленте на полоску. */
const thumbStrips = new WeakMap<HTMLElement, LazyThumbStrip>();

function syncThumbStrip(thumbs: HTMLElement): void {
  let strip = thumbStrips.get(thumbs);
  if (!strip) {
    strip = createLazyThumbStrip(thumbs, ".wb-pdf-thumbs__btn");
    thumbStrips.set(thumbs, strip);
  }
  strip.refresh();
}

export type PdfRenderOptions = {
  assetContentUrl: (assetId: string) => string;
  pagePreviewUrl?: (objectId: string, pageIndex: number) => string | null;
  canChangePage?: boolean;
  onPageChange?: (objectId: string, page: number) => void;
};

function pdfPageImageUrl(
  obj: SceneObject,
  pageIndex: number,
  opts: PdfRenderOptions
): string | null {
  if (!isPdfDocumentObject(obj)) return null;
  const assetId = obj.pageAssetIds[pageIndex];
  if (typeof assetId === "string") return opts.assetContentUrl(assetId);
  return opts.pagePreviewUrl?.(obj.id, pageIndex) ?? null;
}

function pdfPageVisible(obj: SceneObject, pageIndex: number, opts: PdfRenderOptions): boolean {
  return pdfPageImageUrl(obj, pageIndex, opts) != null;
}

function attachThumbClick(
  btn: HTMLButtonElement,
  obj: SceneObject,
  pageIndex: number,
  currentPage: number,
  opts: PdfRenderOptions
) {
  if (btn.dataset.pdfThumbBound === "1") return;
  if (!opts.canChangePage || !opts.onPageChange) return;
  btn.dataset.pdfThumbBound = "1";
  btn.addEventListener("pointerdown", stopToolGesture);
  btn.addEventListener("mousedown", (ev) => {
    ev.preventDefault();
    stopToolGesture(ev);
  });
  btn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    opts.onPageChange!(obj.id, pageIndex);
  });
}

function syncPdfThumbButton(
  btn: HTMLButtonElement,
  obj: SceneObject,
  pageIndex: number,
  currentPage: number,
  canChange: boolean,
  opts: PdfRenderOptions
) {
  const imageUrl = pdfPageImageUrl(obj, pageIndex, opts);
  const active = pageIndex === currentPage;
  btn.classList.toggle("is-active", active);
  btn.setAttribute("aria-current", active ? "true" : "false");
  btn.disabled = !canChange || !imageUrl;
  btn.title = imageUrl ? `Слайд ${pageIndex + 1}` : `Слайд ${pageIndex + 1} (загрузка…)`;

  // Саму картинку вешает лента: в памяти держим только видимые слайды, и не
  // исходники, а уменьшенные копии. Раньше на телефоне картинок в полоске не
  // было вовсе — только номера на пустых карточках.
  if (imageUrl) btn.dataset.thumbSrc = imageUrl;
  else delete btn.dataset.thumbSrc;

  if (canChange && imageUrl) attachThumbClick(btn, obj, pageIndex, currentPage, opts);
}

function borderWidth(obj: SceneObject): number {
  const style = obj.style as { borderWidth?: number } | undefined;
  return typeof style?.borderWidth === "number" ? style.borderWidth : 1;
}

function borderColor(obj: SceneObject): string {
  const style = obj.style as { borderColor?: string } | undefined;
  return style?.borderColor ?? "#cbd5e1";
}

function stopToolGesture(ev: Event) {
  ev.stopPropagation();
}

const thumbsScrollByPdfId = new Map<string, { top: number; left: number }>();

function readThumbsScroll(el: Element): { top: number; left: number } {
  const node = el as HTMLElement;
  return { top: node.scrollTop || 0, left: node.scrollLeft || 0 };
}

function writeThumbsScroll(el: Element, pos: { top: number; left: number }) {
  const node = el as HTMLElement;
  node.scrollTop = pos.top;
  node.scrollLeft = pos.left;
}

export function rememberPdfThumbsScroll(root: ParentNode | null | undefined) {
  if (!root || !("querySelectorAll" in root)) return;
  root.querySelectorAll(".wb-pdf-thumbs").forEach((el) => {
    const id = el.getAttribute("data-pdf-id");
    if (!id) return;
    thumbsScrollByPdfId.set(id, readThumbsScroll(el));
  });
}

export function restorePdfThumbsScroll(root: ParentNode | null | undefined) {
  if (!root || !("querySelectorAll" in root)) return;
  root.querySelectorAll(".wb-pdf-thumbs").forEach((el) => {
    const id = el.getAttribute("data-pdf-id");
    if (!id) return;
    const pos = thumbsScrollByPdfId.get(id);
    if (!pos) return;
    writeThumbsScroll(el, pos);
  });
}

function persistThumbsScroll(thumbs: HTMLElement, pdfId: string) {
  const saved = thumbsScrollByPdfId.get(pdfId);
  if (saved) writeThumbsScroll(thumbs, saved);
  thumbs.addEventListener(
    "scroll",
    () => {
      thumbsScrollByPdfId.set(pdfId, readThumbsScroll(thumbs));
    },
    { passive: true }
  );
  requestAnimationFrame(() => {
    const again = thumbsScrollByPdfId.get(pdfId);
    if (again) writeThumbsScroll(thumbs, again);
  });
}

/**
 * Ставит полоску слайдов вплотную к картинке слайда.
 *
 * Считаем от текущего размера объекта и текущей картинки: раньше место
 * полоски задавалось один раз при создании, и после изменения размера
 * презентации полоска оставалась висеть в стороне — иногда в полдоски от неё.
 */
function layoutThumbsStrip(root: HTMLElement, w: number, h: number): void {
  const thumbs = root.querySelector(".wb-pdf-thumbs") as HTMLElement | null;
  if (!thumbs) return;
  const img = root.querySelector(".wb-scene-obj__inner--pdf img") as HTMLImageElement | null;
  const rect = containedImageRect(w, h, img?.naturalWidth ?? 0, img?.naturalHeight ?? 0);
  // Отступ считаем от края самой картинки слайда, а не от края рамки объекта:
  // при другом соотношении сторон между ними остаются пустые поля.
  const right = Math.round(rect.offsetX + rect.displayW);
  thumbs.style.left = `${Math.min(w, right) + PDF_THUMBS_GAP}px`;
  thumbs.style.top = `${Math.max(0, Math.round(rect.offsetY))}px`;
  thumbs.style.width = `${PDF_THUMBS_WIDTH}px`;
  thumbs.style.height = `${Math.max(80, Math.round(rect.displayH))}px`;
}

export function pdfDomMatchesObject(g: HTMLElement, obj: SceneObject): boolean {
  if (!isPdfDocumentObject(obj)) return false;
  const wrap = g.querySelector(".wb-scene-obj__inner--pdf");
  if (!wrap) return false;
  const pageCount = obj.pageCount;
  if (pageCount <= 1) return true;
  const thumbs = g.querySelector(".wb-pdf-thumbs");
  if (!thumbs) return false;
  const expected = pageCount;
  return thumbs.querySelectorAll(".wb-pdf-thumbs__btn").length === expected;
}

export function syncPdfDocumentOnGroup(
  g: HTMLElement,
  obj: SceneObject,
  opts: PdfRenderOptions
): boolean {
  if (!pdfDomMatchesObject(g, obj) || !isPdfDocumentObject(obj)) return false;

  const w = typeof obj.w === "number" ? obj.w : 640;
  const h = typeof obj.h === "number" ? obj.h : 480;
  const pageCount = obj.pageCount;
  const currentPage = typeof obj.currentPage === "number" ? obj.currentPage : 0;
  const canChange = Boolean(opts.canChangePage && opts.onPageChange);
  const mainImageUrl = pdfPageImageUrl(obj, currentPage, opts);
  const pageReady = mainImageUrl != null;

  g.style.left = `${Math.round(Number(obj.x) || 0)}px`;
  g.style.top = `${Math.round(Number(obj.y) || 0)}px`;
  g.style.width = `${w}px`;
  g.style.height = `${h}px`;

  const wrap = g.querySelector(".wb-scene-obj__inner--pdf") as HTMLElement | null;
  if (!wrap) return false;

  wrap.style.width = `${w}px`;
  wrap.style.height = `${h}px`;
  wrap.style.border = `${borderWidth(obj)}px solid ${borderColor(obj)}`;
  wrap.classList.toggle("is-pdf-page-loading", !pageReady);

  const img = wrap.querySelector("img") as HTMLImageElement | null;
  if (img && mainImageUrl) {
    if (img.getAttribute("src") !== mainImageUrl) img.src = mainImageUrl;
    img.hidden = false;
  } else if (img) {
    img.removeAttribute("src");
    img.hidden = true;
  }

  let loadingEl = wrap.querySelector(".wb-pdf-page-loading") as HTMLElement | null;
  if (!pageReady) {
    if (!loadingEl) {
      loadingEl = document.createElement("div");
      loadingEl.className = "wb-pdf-page-loading";
      loadingEl.textContent = "Загрузка страницы…";
      wrap.insertBefore(loadingEl, wrap.firstChild);
    }
  } else if (loadingEl) {
    loadingEl.remove();
  }

  const navBtns = wrap.querySelectorAll(".wb-pdf-nav__btn");
  const prev = navBtns[0] as HTMLButtonElement | undefined;
  const label = wrap.querySelector(".wb-pdf-nav__label");
  const next = navBtns[1] as HTMLButtonElement | undefined;
  const nav = wrap.querySelector(".wb-pdf-nav") as HTMLElement | null;
  if (nav) {
    nav.dataset.currentPage = String(currentPage);
    nav.dataset.pageCount = String(pageCount);
  }
  if (label) label.textContent = `${currentPage + 1} / ${pageCount}`;
  if (prev) {
    prev.disabled = !canChange || currentPage <= 0;
  }
  if (next) {
    next.disabled = !canChange || currentPage >= pageCount - 1;
  }

  const thumbs = g.querySelector(".wb-pdf-thumbs") as HTMLElement | null;
  if (thumbs && pageCount > 1) {
    const pos = thumbsScrollByPdfId.get(obj.id) ?? readThumbsScroll(thumbs);
    thumbs.querySelectorAll(".wb-pdf-thumbs__btn").forEach((btn, i) => {
      syncPdfThumbButton(btn as HTMLButtonElement, obj, i, currentPage, canChange, opts);
    });
    writeThumbsScroll(thumbs, pos);
    thumbsScrollByPdfId.set(obj.id, pos);
    layoutThumbsStrip(g, w, h);
    syncThumbStrip(thumbs);
  }

  return true;
}

/** Размер картинки внутри рамки при object-fit: contain (без пустых полей). */
function containedImageRect(
  boxW: number,
  boxH: number,
  imgW: number,
  imgH: number
): { displayW: number; displayH: number; offsetX: number; offsetY: number } {
  const iw = imgW > 0 ? imgW : 16;
  const ih = imgH > 0 ? imgH : 9;
  const boxAspect = boxW / Math.max(1, boxH);
  const imgAspect = iw / ih;
  if (imgAspect > boxAspect) {
    // Картинка шире рамки: пустые поля сверху и снизу.
    const displayH = boxW / imgAspect;
    return {
      displayW: boxW,
      displayH,
      offsetX: 0,
      offsetY: (boxH - displayH) / 2,
    };
  }
  // Картинка уже рамки: пустые поля слева и справа. Раньше их не считали, и
  // полоска слайдов вставала по краю рамки — с виду в стороне от слайда.
  const displayW = boxH * imgAspect;
  return {
    displayW,
    displayH: boxH,
    offsetX: (boxW - displayW) / 2,
    offsetY: 0,
  };
}

export function renderPdfDocumentObject(
  obj: SceneObject,
  opts: PdfRenderOptions
): HTMLElement | null {
  if (!isPdfDocumentObject(obj)) return null;
  const w = typeof obj.w === "number" ? obj.w : 640;
  const h = typeof obj.h === "number" ? obj.h : 480;
  const pageCount = obj.pageCount;
  const currentPage = typeof obj.currentPage === "number" ? obj.currentPage : 0;
  const pageAssetIds = Array.isArray(obj.pageAssetIds) ? obj.pageAssetIds : [];
  const canChange = Boolean(opts.canChangePage && opts.onPageChange);
  const mainImageUrl = pdfPageImageUrl(obj, currentPage, opts);
  const pageReady = mainImageUrl != null;
  if (!pageAssetIds.length) return null;

  const g = document.createElement("div");
  g.setAttribute("class", "wb-scene-obj wb-scene-obj--pdf");
  g.setAttribute("data-object-id", obj.id);
  // Место объекта — обычные координаты доски: слой камеры уже сдвинут и
  // масштабирован, поэтому единица здесь равна единице сцены.
  g.style.position = "absolute";
  g.style.left = `${Math.round(Number(obj.x) || 0)}px`;
  g.style.top = `${Math.round(Number(obj.y) || 0)}px`;
  g.style.width = `${w}px`;
  g.style.height = `${h}px`;
  // Полоска слайдов выходит за правый край объекта — обрезать её нельзя.
  g.style.overflow = "visible";

  const wrap = document.createElement("div");
  wrap.setAttribute("class", "wb-scene-obj__inner wb-scene-obj__inner--pdf");
  wrap.style.position = "relative";
  wrap.style.width = `${w}px`;
  wrap.style.height = `${h}px`;
  wrap.style.boxSizing = "border-box";
  wrap.style.overflow = "hidden";
  wrap.style.border = `${borderWidth(obj)}px solid ${borderColor(obj)}`;
  if (!pageReady) wrap.classList.add("is-pdf-page-loading");

  if (!pageReady) {
    const loadingEl = document.createElement("div");
    loadingEl.className = "wb-pdf-page-loading";
    loadingEl.textContent = "Загрузка страницы…";
    wrap.appendChild(loadingEl);
  }

  const img = document.createElement("img");
  if (mainImageUrl) {
    img.src = mainImageUrl;
  }
  img.alt = obj.title ? String(obj.title) : "PDF";
  img.draggable = false;
  img.style.display = pageReady ? "block" : "none";
  img.style.width = "100%";
  img.style.height = "100%";
  img.style.objectFit = "contain";
  wrap.appendChild(img);

  const nav = document.createElement("div");
  nav.setAttribute("class", "wb-pdf-nav");
  nav.setAttribute("data-pdf-id", obj.id);
  nav.dataset.currentPage = String(currentPage);
  nav.dataset.pageCount = String(pageCount);

  const prev = document.createElement("button");
  prev.type = "button";
  prev.className = "wb-pdf-nav__btn";
  prev.textContent = "‹";
  prev.title = "Предыдущая страница";
  prev.disabled = !canChange || currentPage <= 0;

  const label = document.createElement("span");
  label.className = "wb-pdf-nav__label";
  label.textContent = `${currentPage + 1} / ${pageCount}`;

  const next = document.createElement("button");
  next.type = "button";
  next.className = "wb-pdf-nav__btn";
  next.title = "Следующая страница";
  next.textContent = "›";
  next.disabled = !canChange || currentPage >= pageCount - 1;

  if (canChange) {
    const readPage = () => {
      const cur = Number(nav.dataset.currentPage);
      const total = Number(nav.dataset.pageCount);
      return {
        cur: Number.isFinite(cur) ? cur : 0,
        total: Number.isFinite(total) ? total : pageCount,
      };
    };
    const blockSelect = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    prev.addEventListener("pointerdown", blockSelect);
    next.addEventListener("pointerdown", blockSelect);
    prev.addEventListener("mousedown", blockSelect);
    next.addEventListener("mousedown", blockSelect);
    prev.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const { cur } = readPage();
      if (cur > 0) opts.onPageChange!(obj.id, cur - 1);
    });
    next.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const { cur, total } = readPage();
      if (cur < total - 1) opts.onPageChange!(obj.id, cur + 1);
    });
  }

  nav.appendChild(prev);
  nav.appendChild(label);
  nav.appendChild(next);
  wrap.appendChild(nav);

  wrap.style.position = "absolute";
  wrap.style.left = "0";
  wrap.style.top = "0";
  g.appendChild(wrap);

  // Полоска справа = высота самой картинки слайда (без пустых полей сверху/снизу).
  if (pageCount > 1) {
    const thumbs = document.createElement("div");
    thumbs.setAttribute("class", "wb-pdf-thumbs");
    thumbs.style.position = "absolute";
    thumbs.setAttribute("data-pdf-id", obj.id);
    thumbs.setAttribute("aria-label", "Слайды презентации");

    // Размер картинки известен только после загрузки — тогда полоска встаёт
    // ровно по слайду. Размер объекта берём текущий, а не тот, что был при
    // создании: за время загрузки презентацию могли и подвинуть, и растянуть.
    const syncStripToMainImage = () => {
      const pos = thumbsScrollByPdfId.get(obj.id) ?? readThumbsScroll(thumbs);
      const curW = parseFloat(wrap.style.width) || w;
      const curH = parseFloat(wrap.style.height) || h;
      layoutThumbsStrip(g, curW, curH);
      writeThumbsScroll(thumbs, pos);
      thumbsScrollByPdfId.set(obj.id, pos);
    };
    img.addEventListener("load", syncStripToMainImage);

    for (let i = 0; i < pageCount; i++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className =
        "wb-pdf-thumbs__btn" + (i === currentPage ? " is-active" : "");
      btn.setAttribute("aria-current", i === currentPage ? "true" : "false");

      const num = document.createElement("span");
      num.className = "wb-pdf-thumbs__num";
      num.textContent = String(i + 1);
      btn.appendChild(num);

      syncPdfThumbButton(btn, obj, i, currentPage, canChange, opts);
      thumbs.appendChild(btn);
    }

    g.appendChild(thumbs);
    layoutThumbsStrip(g, w, h);
    persistThumbsScroll(thumbs, obj.id);
    syncThumbStrip(thumbs);

    // Мини-слайды — только над полоской; основной слайд — pan/zoom доски.
    thumbs.addEventListener(
      "wheel",
      (ev) => {
        if (ev.ctrlKey || ev.metaKey) return;
        ev.stopPropagation();
      },
      { passive: true }
    );
  }

  return g;
}
